import { useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Mic, Loader2 } from "lucide-react";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { voiceApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";

const CREATE_ROUTES = {
  customer: "/customers?new=1",
  task: "/tasks?new=1",
  serial: "/serial-numbers?new=1",
  "service-report": "/service-reports/new",
};

const speak = (text) => {
  if (!text || typeof window === "undefined" || !window.speechSynthesis) return;
  try {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "de-DE";
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch {
    // ignore
  }
};

/**
 * Globaler Sprach-Assistent: nimmt einen gesprochenen Befehl auf, lässt ihn vom
 * Backend (LLM) interpretieren und führt Navigation / Erstellen / Suche aus.
 */
export function VoiceAssistant() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [processing, setProcessing] = useState(false);
  const busyRef = useRef(false);

  const handleCommand = async (text) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setProcessing(true);
    try {
      const { data } = await voiceApi.command(text);
      if (data.speak) speak(data.speak);

      if (data.action === "navigate" && data.route) {
        toast.success(data.speak || "Öffne …");
        navigate(data.route);
      } else if (data.action === "call") {
        if (data.phone) {
          toast.success(data.speak || `Rufe ${data.customer_name || ""} an`);
          window.location.href = `tel:${data.phone}`;
        } else if (data.customer_id) {
          toast.info(data.speak || "Keine Nummer hinterlegt – öffne Kunde");
          navigate(`/customers/${data.customer_id}`);
        } else {
          toast.error(data.speak || "Kunde nicht gefunden");
        }
      } else if (data.action === "open_customer") {
        if (data.customer_id) {
          toast.success(data.speak || "Öffne Kunde");
          navigate(`/customers/${data.customer_id}`);
        } else {
          toast.error(data.speak || "Kunde nicht gefunden");
        }
      } else if (data.action === "create" && data.entity === "task") {
        const params = new URLSearchParams({ new: "1" });
        if (data.title) params.set("title", data.title);
        if (data.customer_id) params.set("customer", data.customer_id);
        toast.success(data.speak || "Aufgabe wird angelegt");
        navigate(`/tasks?${params.toString()}`);
      } else if (data.action === "create" && data.entity === "service-report") {
        toast.success(data.speak || "Neuer Servicebericht");
        navigate(data.customer_id ? `/service-reports/new?customer=${data.customer_id}` : "/service-reports/new");
      } else if (data.action === "create" && CREATE_ROUTES[data.entity]) {
        toast.success(data.speak || "Wird angelegt …");
        navigate(CREATE_ROUTES[data.entity]);
      } else if (data.action === "search" && data.query) {
        toast.success(data.speak || `Suche nach ${data.query}`);
        if (data.target === "serial") {
          navigate(`/serial-numbers?q=${encodeURIComponent(data.query)}`);
        } else {
          navigate(`/customers?q=${encodeURIComponent(data.query)}`);
        }
      } else {
        toast.error(data.speak || `Nicht verstanden: „${text}“`);
      }
    } catch {
      toast.error("Sprachbefehl konnte nicht verarbeitet werden");
    } finally {
      setProcessing(false);
      busyRef.current = false;
    }
  };

  const { listening, supported, start, stop } = useSpeechRecognition({
    continuous: false,
    onResult: handleCommand,
    onError: (e) => {
      if (e?.error === "not-allowed" || e?.error === "service-not-allowed") {
        toast.error("Mikrofon-Zugriff verweigert");
      }
    },
  });

  if (!supported) return null;

  // Der schwebende Mic-Button würde auf Seiten mit fest verankerter unterer
  // Eingabeleiste (Team-Chat) den Senden-Button überlappen – dort ausblenden.
  const HIDE_ON = ["/chat"];
  if (HIDE_ON.some((p) => location.pathname.startsWith(p))) return null;

  return (
    <button
      type="button"
      onClick={listening ? stop : start}
      aria-label="Sprachsteuerung"
      title={listening ? "Höre zu … (tippen zum Stoppen)" : "Sprachsteuerung starten"}
      aria-pressed={listening}
      data-testid="voice-assistant-btn"
      className={`fixed z-40 h-14 w-14 rounded-full shadow-lg flex items-center justify-center active:scale-95 transition-transform
        right-4 bottom-[calc(4rem+3.5rem+1.5rem+env(safe-area-inset-bottom))]
        lg:left-auto lg:right-6 lg:bottom-6
        ${listening ? "bg-red-600 text-white animate-pulse shadow-red-500/40" : "bg-slate-800 text-white shadow-slate-900/30 hover:bg-slate-700"}`}
    >
      {processing ? (
        <Loader2 className="w-6 h-6 animate-spin" />
      ) : (
        <Mic className="w-6 h-6" strokeWidth={2.2} />
      )}
    </button>
  );
}

export default VoiceAssistant;
